import { createFileRoute } from "@tanstack/react-router";
import { byCategory } from "@/data/products";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { ProductGrid } from "@/components/site/product-grid";
import { FinalCTA } from "@/components/site/final-cta";
import { pageUrl } from "@/data/site";

export const Route = createFileRoute("/cnc-engraved")({
  head: () => ({
    meta: [
      { title: "CNC Carved Signs & Engraved Gifts | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "Custom CNC-carved signs, 3D relief carvings and personalized engraving, cut and finished by hand in Paragould, Arkansas.",
      },
      { property: "og:title", content: "CNC-carved signs and personalized engraving" },
      {
        property: "og:description",
        content: "Signs, 3D carvings and engraved gifts cut to your wording and finished by hand.",
      },
      { property: "og:url", content: pageUrl("/cnc-engraved") },
    ],
    links: [{ rel: "canonical", href: pageUrl("/cnc-engraved") }],
  }),
  component: CncEngraved,
});

const sizes = [
  { label: "Small", body: "Desk plaques, name blocks and gift boards — roughly up to 12 inches." },
  { label: "Medium", body: "Family name signs and relief panels for a wall — roughly 12 to 24 inches." },
  { label: "Large", body: "Entry signs and statement pieces. Confirm maximum size with Paul before designing." },
];

function CncEngraved() {
  const items = byCategory("cnc");
  return (
    <>
      <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "CNC & Engraved" }]} />
      <PageHeader
        eyebrow="CNC & engraved"
        title="Cut with a machine. Finished by hand."
        lede="Paul added a CNC router to the shop in 2019. It handles the precision; he handles the layout, the sanding, the paint and the judgment calls."
      >
        <CtaLink to="/design-your-piece" arrow className="h-12">
          Design Your Piece
        </CtaLink>
      </PageHeader>

      <Section tone="paper" labelledBy="cnc-what-heading">
        <div className="shell">
          <SectionHeading id="cnc-what-heading" eyebrow="What Paul cuts" title="Three kinds of work." />
          <div className="mt-12 grid gap-px border border-hairline bg-hairline lg:grid-cols-3">
            <article id="signs" className="bg-background p-7">
              <h3 className="font-[family-name:var(--font-display)] text-2xl">Signs</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Family names, addresses, businesses, cabins and gifts. Wording, typeface and finish are agreed
                with you before anything is cut.
              </p>
            </article>
            <article id="carvings" className="bg-background p-7">
              <h3 className="font-[family-name:var(--font-display)] text-2xl">3D carvings</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Layered relief work cut in several passes and cleaned up by hand. Wildlife, scenes, emblems and
                lettering all carve well.
              </p>
            </article>
            <article id="engraving" className="bg-background p-7">
              <h3 className="font-[family-name:var(--font-display)] text-2xl">Engraving</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Names, dates and short messages cut into boards, plaques and gifts. Cut rather than burned, so
                the detail stays crisp.
              </p>
            </article>
          </div>
        </div>
      </Section>

      <Section labelledBy="cnc-work-heading">
        <div className="shell">
          <SectionHeading
            id="cnc-work-heading"
            eyebrow="Previous projects"
            title="Work off the router."
            lede="Each of these was designed around someone's wording and space. Yours would be too."
          />
          <ProductGrid products={items} />
        </div>
      </Section>

      <Section tone="paper" labelledBy="cnc-spec-heading">
        <div className="shell grid gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading
              id="cnc-spec-heading"
              eyebrow="Sizes and materials"
              title="What it can be cut from."
              lede="Hardwoods such as cherry, walnut and maple carve the cleanest. Paint-grade panels suit signs that will be finished in color."
            />
            <dl className="mt-8 divide-y divide-hairline border-y border-hairline">
              {sizes.map((s) => (
                <div key={s.label} className="grid grid-cols-[6rem_1fr] gap-4 py-4">
                  <dt className="text-sm font-semibold">{s.label}</dt>
                  <dd className="text-sm text-muted-foreground">{s.body}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-5 text-xs text-muted-foreground">
              Exact maximum cutting dimensions are confirmed with Paul for each project.
            </p>
          </div>

          <div className="surface-card p-8">
            <h2 className="display-md">Design limitations, honestly</h2>
            <ul className="mt-5 space-y-4 text-sm leading-relaxed text-muted-foreground">
              <li>
                <strong className="text-ink">Very fine detail has a floor.</strong> Below a certain size the bit
                cannot reach into the detail. Paul will suggest a larger panel or a simplified design instead of
                cutting something that will look muddy.
              </li>
              <li>
                <strong className="text-ink">Grain matters.</strong> Open-grained woods blur small lettering.
                Tight-grained hardwoods hold it.
              </li>
              <li>
                <strong className="text-ink">Artwork quality matters.</strong> A crisp vector or a high
                resolution image carves well; a small screenshot does not.
              </li>
              <li>
                <strong className="text-ink">Trademarks and copyrighted logos.</strong> Paul does not reproduce
                logos or artwork you do not own or have written permission to use. Bring the permission and he
                will cut it.
              </li>
            </ul>
            <div className="mt-7">
              <CtaLink to="/design-your-piece" arrow className="h-12">
                Send Paul your artwork
              </CtaLink>
            </div>
          </div>
        </div>
      </Section>

      <FinalCTA heading="Send the wording, the size and the space. Paul will handle the rest." />
    </>
  );
}
